/**
 * The game's name, as a mark.
 *
 * The wordless rule was written as "no words anywhere", and the reason behind it was always narrower than
 * that: a word she is *expected to read* is either decoration she ignores or an instruction she cannot
 * follow. The game's own name is neither. It is the thing that makes the Lookout read as a finished game
 * rather than a screen of buttons - to her as a shape and a colour she comes to recognise, and to the adult
 * holding the tablet as the name of what their child is playing. So the rule is now about copy, and a title
 * is allowed.
 *
 * What is still enforced is that it behaves like artwork rather than like text:
 *
 *  - it carries `data-brand`, and the sweeps (`tests/loop-screens.test.ts`,
 *    `tests/loop-walkthrough.test.ts`) skip that one subtree and hold everything else to not one letter -
 *    so easing the rule for the title cannot quietly ease it for a label somewhere else;
 *  - it is `aria-hidden` and `pointer-events: none`: not a control, not a label, and never in front of a
 *    slot;
 *  - it says nothing the game needs. If it fails to build, the Lookout is exactly the screen it was.
 *
 * Drawn here rather than fetched from the content pack, unlike every icon: the pack is swappable art, and
 * the name of the game is not. It is also the one piece of the screen that must be there on the first frame
 * - a title that fades in half a second late is a title she watches load.
 *
 * The letters curve because that is the whole reason the mark works at this size: an arc over the Lookout
 * reads as a badge, and a straight line of text over it reads as a caption.
 */

/** The name, once. Uppercased for the mark; this is the form a parent would read. */
export const GAME_NAME = 'Millie & Aaron Patrol';

/**
 * The baseline the letters sit on: a shallow arc, bulging up, ending low at both shoulders so the mark
 * frames the tower rather than sitting in a bar above it.
 *
 * Exported as its three points as well, because the pack alphabet cannot use the path: `textPath` sets
 * *text* along a curve, and a traced glyph is a shape. `src/shell/brand-alphabet.ts` places each letter
 * along this same quadratic by hand, and the two marks have to curve identically or swapping one for the
 * other moves the name.
 */
const ARC = 'M 74 166 Q 500 32 926 166';
export const ARC_POINTS = {
  from: { x: 74, y: 166 },
  control: { x: 500, y: 32 },
  to: { x: 926, y: 166 },
} as const;

/** The box both marks are drawn in. */
export const MARK_VIEWBOX = '0 0 1000 200';

/**
 * How much of the arc the name is stretched to fill.
 *
 * `textLength` is what makes the mark the same drawing on both tablets. There is no webfont here - a PWA
 * that must work offline gets the system face - and iPadOS sets this in SF while Android sets it in Roboto,
 * which are different widths. Left to itself the name would overhang the arc on one device and float short
 * of it on the other. Told the length, both draw it to the same width and the arc is the only thing
 * deciding where the letters go.
 */
const TEXT_LENGTH = 812;

const INK = '#1f1f1f';
const PAW_RED = '#e63946';

/** The five shapes of a paw print, in a local frame centred on the pad. */
const PAW_SHAPES = [
  '<ellipse cx="0" cy="14" rx="18" ry="15"/>',
  '<ellipse cx="-17" cy="-5" rx="8.5" ry="10.5" transform="rotate(-18 -17 -5)"/>',
  '<ellipse cx="-6" cy="-16" rx="8" ry="10" transform="rotate(-7 -6 -16)"/>',
  '<ellipse cx="6" cy="-16" rx="8" ry="10" transform="rotate(7 6 -16)"/>',
  '<ellipse cx="17" cy="-5" rx="8.5" ry="10.5" transform="rotate(18 17 -5)"/>',
].join('');

/**
 * A paw print at the end of the arc.
 *
 * Flat colour, then one ink outline over the top - the pack's house style from docs/ART_PIPELINE.md, so the
 * mark belongs to the same family as the icons underneath it. The shapes are emitted twice for that reason;
 * a single element with both a fill and a stroke paints the stroke over the fill and eats the shape.
 */
export function paw(transform: string): string {
  return (
    `<g transform="${transform}">` +
    `<g fill="${PAW_RED}">${PAW_SHAPES}</g>` +
    `<g fill="none" stroke="${INK}" stroke-width="5" stroke-linejoin="round">${PAW_SHAPES}</g>` +
    `</g>`
  );
}

/**
 * One pass of the name along the arc.
 *
 * Three passes make the letterform: an offset silhouette for depth, a fat ink outline, then the gold face.
 * Same reason as the paw - fill and stroke on one element would draw the outline *inside* the letters and
 * leave them spindly, which at a glance is the difference between a logo and a caption.
 */
function words(name: string, attrs: string, dy = 0): string {
  const shift = dy === 0 ? '' : ` transform="translate(0 ${dy})"`;
  /*
   * The face is picked in the stylesheet (`.brand-mark text`), not here. A presentation attribute cannot
   * resolve `var(--font)`, and writing the stack out would be a second copy of the token that only has to
   * disagree once for the name to be set in something other than the rest of the game.
   */
  return (
    `<text${shift} x="0" y="0" text-anchor="middle" font-size="66" font-weight="900" ${attrs}>` +
    `<textPath href="#brand-arc" xlink:href="#brand-arc" startOffset="50%"` +
    ` textLength="${TEXT_LENGTH}" lengthAdjust="spacingAndGlyphs">${name}</textPath>` +
    `</text>`
  );
}

function escapeXml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function markup(name: string): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"` +
    ` viewBox="${MARK_VIEWBOX}" class="brand-mark" aria-hidden="true" data-brand="title">` +
    `<defs>` +
    `<path id="brand-arc" d="${ARC}"/>` +
    `<linearGradient id="brand-face" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0" stop-color="#fff3b0"/>` +
    `<stop offset="0.46" stop-color="#ffc32b"/>` +
    `<stop offset="1" stop-color="#f28c1c"/>` +
    `</linearGradient>` +
    `</defs>` +
    paw('translate(48 148) rotate(-16) scale(0.92)') +
    paw('translate(952 148) rotate(16) scale(0.92)') +
    // Depth first: the same letters in navy, dropped a few units, so the gold sits proud of the sky.
    words(
      name,
      'fill="#14315c" fill-opacity="0.34" stroke="#14315c" stroke-opacity="0.34"' +
        ' stroke-width="20" stroke-linejoin="round"',
      10,
    ) +
    words(name, `fill="none" stroke="${INK}" stroke-width="20" stroke-linejoin="round" stroke-linecap="round"`) +
    words(name, 'fill="url(#brand-face)"') +
    `</svg>`
  );
}

/**
 * The mark, or null.
 *
 * Null is an outcome the caller must survive rather than an error, exactly like a missing icon: the mark
 * is the one thing on this screen that carries no meaning she needs, so a screen without it is a screen
 * with one fewer decoration and every control intact.
 */
export function brandMark(name = GAME_NAME): SVGSVGElement | null {
  const parsed = new DOMParser().parseFromString(markup(escapeXml(name.toUpperCase())), 'image/svg+xml');
  if (parsed.querySelector('parsererror') !== null) return null;
  return parsed.documentElement as unknown as SVGSVGElement;
}
